import React, { useState } from "react";
import {
  Box,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ReactComponent as Cursor } from "../static/Icons/cursor.svg";
import { ReactComponent as Claude } from "../static/Icons/claude.svg";
import { ReactComponent as Windsurf } from "../static/Icons/windsurf.svg";
import { ReactComponent as VSCode } from "../static/Icons/vscode.svg";
import { ReactComponent as Cline } from "../static/Icons/cline.svg";
import CopyComponent from "../CommonComponents/CopyComponent";
import { serverUpdateSecret } from "../api/server";
import { showErrorToast, showSuccessToast } from "../toastUtils";
import ButtonCommon from "../CommonComponents/ButtonCommon";
import ReportGmailerrorredIcon from "@mui/icons-material/ReportGmailerrorred";
import { getMcpServerUrl, getMcpServerDisplayUrl } from "../utils/mcpUrl";

export default function ConnectPage() {
  const connectTabs = [
    { label: "Cursor", icon: Cursor },
    { label: "Claude", icon: Claude },
    { label: "Windsurf", icon: Windsurf },
    { label: "VS Code", icon: VSCode },
    { label: "Cline", icon: Cline },
  ];

  const [selectedTab, setSelectedTab] = useState(0);
  const [serverSecrets, setServerSecrets] = useState(null);
  const [serverId, setServerId] = useState(null);
  const [loading, setLoading] = useState(false);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  const handleConnectTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  // const fetchServerSecrets = async () => {
  //   try {
  //     const res = await getServerSecrets();
  //     if (res.success) {
  //       setServerSecrets(res.data.server_secrets);
  //       setServerId(res.data.server_id);
  //     } else {
  //       showErrorToast(res.message);
  //     }
  //   } catch (error) {
  //     showErrorToast(error.message);
  //   }
  // };

  const handleRotate = async () => {
    setLoading(true);
    try {
      const res = await serverUpdateSecret();
      if (res.success) {
        setServerSecrets(res.data.server_secrets);
        setServerId(res.data.server_id);
        showSuccessToast(res.message);
        setLoading(false);
      } else {
        showErrorToast(res.message);
        setLoading(false);
      }
    } catch (error) {
      showErrorToast(error.message);
      setLoading(false);
    }
  };

  // const hasRun = React.useRef(false);

  // useEffect(() => {
  //   if (!hasRun.current) {
  //     hasRun.current = true;
  //     fetchServerSecrets();
  //   }
  // }, []);

  const getStepsForClient = (clientName) => {
    const stepsConfig = {
      Cursor: [
        "Navigate to Settings, then Cursor Settings",
        "Select MCP on the left",
        "Click Add new global MCP server at the top right",
        "Copy and paste the server config to your existing file, then save",
      ],
      Claude: [
        "Open Claude Desktop application",
        "Navigate to Settings > Model Context Protocol",
        "Click 'Add Server' button",
        "Enter server name as 'MCPCloud' and paste the server URL",
        "Save the configuration and restart Claude Desktop",
      ],
      Windsurf: [
        "Open Windsurf IDE",
        "Go to File > Preferences > Settings",
        "Search for 'MCP Servers' in settings",
        "Add new MCP server configuration",
        "Copy and paste the server config, then save",
      ],
      "VS Code": [
        "Install the MCP extension from VS Code marketplace",
        "Open Command Palette (Ctrl+Shift+P / Cmd+Shift+P)",
        "Type 'MCP: Configure Servers'",
        "Add new server configuration",
        "Copy and paste the server config, then save",
      ],
      Cline: [
        "Open Cline extension settings",
        "Navigate to MCP Server configuration",
        "Click 'Add New Server'",
        "Enter server details and paste the configuration",
        "Save and restart the extension",
      ],
    };
    return stepsConfig[clientName] || [];
  };

  const steps = getStepsForClient(connectTabs[selectedTab]?.label);

  return (
    <Paper
      sx={{
        p: { xs: 2, sm: 3 },
        bgcolor: "#D4E6FF17",
        color: "white",
        borderRadius: 2,
        width: "100%",
        overflowX: "hidden",
      }}
    >
      {/* Responsive Tabs with scrolling support */}
      <Box>
        <Tabs
          value={selectedTab}
          onChange={handleConnectTabChange}
          textColor="inherit"
          indicatorColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile={true} 
          sx={{
            minHeight: "48px",
            "& .MuiTabs-scroller": {
              [theme.breakpoints.down("md")]: {
                overflowX: "auto",
                scrollbarWidth: "none", 
                "&::-webkit-scrollbar": {
                  display: "none", 
                },
              },
            },
            "& .MuiTab-root": {
              [theme.breakpoints.down("md")]: {
                flexDirection: "column",
                minHeight: "80px",
                minWidth: "60px",
                maxWidth: "70px", 
                "& .MuiTab-iconWrapper": {
                  marginBottom: "4px",
                  marginRight: 0,
                },
              },
              // For larger screens - keep icon and text horizontal
              [theme.breakpoints.up("md")]: {
                flexDirection: "row",
                minWidth: "120px",
                "& .MuiTab-iconWrapper": {
                  marginRight: "8px",
                  marginBottom: 0,
                },
              },
            },
          }}
        >
          {connectTabs.map((tab, index) => {
            const IconComponent = tab.icon;
            const isSelected = selectedTab === index;
            return (
              <Tab
                key={index}
                label={tab.label}
                icon={
                  <IconComponent
                    style={{
                      color: "white",
                      width: 20, // Slightly smaller icon on mobile
                      height: 20,
                    }}
                  />
                }
                iconPosition={isSmallScreen ? "top" : "start"}
                sx={{
                  fontWeight: isSelected ? "bold" : "normal",
                  flexShrink: 0,
                  // Mobile-specific styling
                  [theme.breakpoints.down("md")]: {
                    minWidth: "70px", // Smaller width
                    maxWidth: "80px",
                    padding: "8px 4px", // Reduced padding
                    fontSize: "0.7rem", // Smaller font
                    lineHeight: 1.2,
                    // Ensure text doesn't wrap
                    "& .MuiTab-label": {
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  },
                  // Desktop styling
                  [theme.breakpoints.up("md")]: {
                    minWidth: "120px",
                    padding: "12px 16px",
                    fontSize: "0.875rem",
                  },
                }}
              />
            );
          })}
        </Tabs>
      </Box>

      {/* Main content */}
      <Box sx={{ py: 3, px: { xs: 1, sm: 2 }, width: "100%" }}>
        <Stack spacing={3}>
          {/* Step list */}
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              STEPS:
            </Typography>
            <List disablePadding sx={{ pl: 2 }}>
              {steps.map((step, index) => (
                <ListItem
                  key={index}
                  disableGutters
                  sx={{
                    display: "list-item",
                    listStyleType: "decimal",
                    pl: 1,
                    py: 0.5,
                    "&:hover": { backgroundColor: "transparent" },
                    wordWrap: "break-word",
                  }}
                >
                  <ListItemText primary={step} sx={{ m: 0 }} />
                </ListItem>
              ))}
            </List>
          </Box>

          {/* Config header + button */}
          <Box
            display="flex"
            flexDirection={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            gap={2}
            sx={{ width: "100%", flexWrap: "wrap" }}
          >
            <Typography variant="h6" gutterBottom>
              MCP server config
            </Typography>
            <ButtonCommon
              sx={{
                width: "100%",
                maxWidth: { xs: "100%", sm: "fit-content" },
              }}
              onClick={handleRotate}
              disabled={loading}
            >
              {loading ? <CircularProgress size={20} /> : "Load Config"}
            </ButtonCommon>
          </Box>

          {/* Warning Box + Copy sections */}
          {serverSecrets !== null && (
            <>
              <Box
                p={2}
                sx={{
                  border: "1.39px solid #7B0001",
                  borderRadius: 2,
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "flex-start",
                  gap: 2,
                  background: "#B9161714",
                  flexWrap: "wrap",
                  width: "100%",
                  overflowWrap: "break-word",
                }}
              >
                <ReportGmailerrorredIcon sx={{ color: "#B91617" }} />
                <Typography
                  sx={{ color: "#B91617", fontSize: "0.9rem", flex: 1 }}
                >
                  If you lose or forget your server secret key, you can not
                  retrieve it. Instead create a new server secret key and make
                  the old key inactive.
                </Typography>
              </Box>

              <Box sx={{ width: "100%", overflowX: "auto" }}>
                <CopyComponent
                  text={`{
  "mcpServers": {
    "MCPCloud": {
      "url": "${getMcpServerUrl(serverId, serverSecrets)}"
    }
  }
}`}
                  displayText={`{
  "mcpServers": {
    "MCPCloud": {
      "url": "${getMcpServerDisplayUrl(serverId, 8)}"
    }
  }
}`}
                />
              </Box>

              <Box sx={{ width: "100%", overflowX: "auto" }}>
                <Typography variant="h6" gutterBottom>
                  MCP server URL
                </Typography>
                <CopyComponent
                  text={getMcpServerUrl(serverId, serverSecrets)}
                  displayText={getMcpServerDisplayUrl(serverId, 8)}
                  isRotate={true}
                  handleRotate={handleRotate}
                />
              </Box>
            </>
          )}
        </Stack>
      </Box>
    </Paper>
  );
}
